"use client";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { Lock, LogOut, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { authService } from "../services/auth";

interface ReauthModalProps {
  isOpen: boolean;
  userEmail?: string;
  onSuccess: () => void;
  onLogout: () => void;
}

export function ReauthModal({ isOpen, userEmail = "", onSuccess, onLogout }: ReauthModalProps) {
  const [email, setEmail] = useState(userEmail);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Mantém o email atualizado se for repassado nas props
  const currentEmail = email || userEmail;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const targetEmail = currentEmail.trim();
    if (!targetEmail || !password.trim()) {
      toast.error("Por favor, informe a senha para continuar.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.login(targetEmail, password);
      const token = response.data.data?.token || response.data.token || response.data.access_token;

      if (token) {
        localStorage.setItem("sanctum_token", token);
        try {
          const oldRes = await authService.oldAPIInitSession(token);
          if (oldRes?.data?.accessToken) {
            localStorage.setItem("accessToken", oldRes.data.accessToken);
          }
        } catch (e) {
          console.error("Old API init session failed during re-auth", e);
        }
      }

      toast.success("Sessão renovada com sucesso!");
      setPassword("");
      onSuccess();
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (error: any) {
      console.error("Re-auth error:", error);
      const message = error.response?.data?.message || "Senha incorreta. Tente novamente.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => { }}>
      <DialogContent className="sm:max-w-md bg-white border border-gray-100 shadow-2xl [&>button]:hidden">
        <DialogHeader className="flex flex-col items-center text-center space-y-3 pt-2">
          <div className="w-14 h-14 rounded-full bg-[#D4C070]/15 flex items-center justify-center text-[#D4C070]">
            <Lock className="w-7 h-7" />
          </div>
          <div>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Sessão Expirada
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1 max-w-xs">
              Sua sessão expirou por inatividade. Digite sua senha para renovar o acesso e continuar de onde parou.
            </p>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="reauth-email" className="text-xs text-gray-700 font-medium">
              E-mail
            </Label>
            <Input
              id="reauth-email"
              type="email"
              value={currentEmail}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="bg-gray-50 text-gray-800"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reauth-password" className="text-xs text-gray-700 font-medium">
              Senha de Acesso
            </Label>
            <div className="relative">
              <Input
                id="reauth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
                className="pr-10"
              />
              <KeyRound className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div className="pt-2 flex flex-col gap-2">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#D4C070] hover:bg-[#c4b060] text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Renovando acesso...
                </>
              ) : (
                "Renovar Sessão"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={onLogout}
              className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-transparent flex items-center justify-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sair e fazer login com outra conta
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
